import { useState, useEffect, useRef } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Box,
  CircularProgress,
} from "@mui/material";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InboxIcon from "@mui/icons-material/Inbox";

import { UrlApi } from "../../../../services/apirest";

const POLL_INTERVAL_MS = 10_000; // 10 segundos

function UploadPadron(props) {

  const ENDPOINT_MAP = {
  2: "upload-arba",
  6: "upload-cdba",
  1: "upload-santafe",
  3: "upload-capitalfederal",
  8: "upload-entrerios",
  9: "upload-jujuy",
  17: props?.provincia?.tipo === "alic-rg11610" ? "upload-tucuman-rg" :  "upload-tucuman"
};

  const [openclose, setOpenClose] = useState(props.openclose);
  const [fileList, setFileList] = useState([]);
  const [percepcion, setPercepcion] = useState(null);
  const [retencion, setRetencion] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Estado del job activo
  const [jobId, setJobId] = useState(null);
  const [jobMessage, setJobMessage] = useState("");
  const intervalRef = useRef(null);
  const prevMessageRef = useRef(""); // evita mostrar el mismo toast dos veces seguidas

  const { showToast } = props;

  const esBuenosAires = props.provincia?.id === 2;
  const esCorddoba = props.provincia?.id === 6;
  const esArbaOCdba = esBuenosAires || esCorddoba;

  useEffect(() => {
    setOpenClose(props.openclose);
  }, [props.openclose]);

  // ── Polling: se activa cuando hay un jobId ─────────────────────────────
  useEffect(() => {
    if (!jobId) return;

    const poll = async () => {
      try {
        const res = await fetch(`${UrlApi}/uploadfiles/job-status/${jobId}`);
        if (!res.ok) return;
        const job = await res.json();
        const msg = job.message || "";
        setJobMessage(msg);

        // Mostrar toast solo cuando el mensaje cambia (evita spam del mismo texto)
        if (msg && msg !== prevMessageRef.current) {
          prevMessageRef.current = msg;

          if (job.status === "pending" || job.status === "processing") {
            showToast(msg, "info");
          }
        }

        if (job.status === "done") {
          clearInterval(intervalRef.current);
          showToast(
            msg || `Padrón de ${props.provincia?.provincia} cargado correctamente`,
            "success",
          );
          resetAfterJob(true);
        } else if (job.status === "error") {
          clearInterval(intervalRef.current);
          showToast(msg || "Error en la carga del padrón", "error");
          // No limpiamos archivos: el usuario puede corregir y reintentar
          resetAfterJob(false);
        }
      } catch (err) {
        console.error("[UploadPadron] Error polling job:", err);
      }
    };

    // Primer poll inmediato para atrapar errores rápidos
    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const resetAfterJob = (clearFiles = true) => {
    setJobId(null);
    setJobMessage("");
    setIsSubmitted(false);
    props.isSubmitting?.(false);
    if (clearFiles) {
      setFileList([]);
      setPercepcion(null);
      setRetencion(null);
    }
  };

  // ── Handlers de archivo ───────────────────────────────────────────────
  const handleFileChange = (e) => setFileList(e.target.files);
  const handleFilePercepcion = (e) => setPercepcion(e.target.files[0]);
  const handleFileRetencion = (e) => setRetencion(e.target.files[0]);

  const removefiles = (idx) => {
    const updated = Array.from(fileList).filter((_, i) => i !== idx);
    setFileList(updated);
  };

  // ── Submit ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setIsSubmitted(true);
    props.isSubmitting?.(true);

    const endpoint = ENDPOINT_MAP[props.provincia?.id];
    if (!endpoint) {
      showToast(
        `Provincia sin endpoint configurado: ${props.provincia?.provincia}`,
        "error",
      );
      setIsSubmitted(false);
      props.isSubmitting?.(false);
      return;
    }

    const data = new FormData();
    data.append("uid", props.fileId);
    data.append("id_provincia", props.provincia?.id);
    data.append("usuario", localStorage.getItem("user"));
    data.append("tipo", props?.provincia?.tipo || "alic-general");

    if (esArbaOCdba) {
      data.append("percepciones", percepcion);
      data.append("retenciones", retencion);
    } else {
      Array.from(fileList).forEach((f) => data.append("archivo", f));
    }

    try {
      const res = await fetch(`${UrlApi}/uploadfiles/${endpoint}`, {
        method: "POST",
        body: data,
      });

      let resData = null;
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("application/json")) resData = await res.json();

      if (res.status === 202) {
        // Archivo válido y aceptado — arrancar polling
        prevMessageRef.current = "";
        setJobId(resData.job_id);
        setJobMessage("Archivo recibido. Procesando en segundo plano...");
        showToast("Archivo recibido. Procesando en segundo plano...", "info");
      } else if (res.status === 400) {
        // Validación de primera línea — respuesta inmediata
        showToast(
          resData?.message ?? "El archivo tiene un formato incorrecto.",
          "error",
        );
        setIsSubmitted(false);
        props.isSubmitting?.(false);
      } else if (res.status === 409) {
        // Ya hay un proceso activo globalmente
        showToast(
          resData?.message ?? "Ya hay un proceso activo. Esperá a que termine.",
          "warning",
        );
        setIsSubmitted(false);
        props.isSubmitting?.(false);
      } else {
        const errorMsg =
          resData?.message ??
          resData?.error ??
          `Error ${res.status} al subir el archivo`;
        showToast(errorMsg, "error");
        setIsSubmitted(false);
        props.isSubmitting?.(false);
      }
    } catch (error) {
      console.error("[UploadPadron] Error de red:", error);
      showToast("Sin respuesta del servidor. Verificá la conexión.", "error");
      setIsSubmitted(false);
      props.isSubmitting?.(false);
    }
  };

  const puedeGuardar = esArbaOCdba
    ? percepcion !== null && retencion !== null
    : fileList.length > 0;

  return (
    <Dialog
      open={openclose}
      // Bloquear cierre mientras procesa para mantener el polling activo
      onClose={isSubmitted ? undefined : props.onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #1F618D 0%, #2980B9 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 2,
        }}
      >
        <CloudUploadIcon />
        <Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1}>
            {props.provincia?.provincia}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            Subir padrón
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* ── Estado del job activo ── */}
        {isSubmitted && jobId && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 5,
              gap: 2,
            }}
          >
            <CircularProgress size={52} />
            <Typography variant="body1" fontWeight={600} textAlign="center">
              Procesando padrón...
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ maxWidth: 340 }}
            >
              {jobMessage ||
                "Esto puede tardar varios minutos. Podés dejar esta ventana abierta."}
            </Typography>
          </Box>
        )}

        {/* ── Formulario de carga (oculto mientras procesa) ── */}
        {!isSubmitted && (
          <>
            {esArbaOCdba ? (
              <Box
                sx={{
                  backgroundColor: "#F4F8FB",
                  border: "1px solid #D6E9F8",
                  borderRadius: 2,
                  p: 2.5,
                  mb: 2,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="#1F618D"
                  display="block"
                  mb={1.5}
                >
                  ARCHIVOS REQUERIDOS
                </Typography>
                <Grid container spacing={2} direction="column">
                  <Grid item>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<ContentPasteSearchIcon />}
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        borderColor: "#1F618D",
                        color: "#1F618D",
                      }}
                    >
                      Percepción (.txt)
                      <input
                        hidden
                        type="file"
                        accept=".txt"
                        onChange={handleFilePercepcion}
                      />
                    </Button>
                    {percepcion && (
                      <TextField
                        disabled
                        value={percepcion.name}
                        size="small"
                        sx={{ ml: 1.5, verticalAlign: "middle", width: 220 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachFileIcon
                                fontSize="small"
                                color="primary"
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<ContentPasteSearchIcon />}
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        borderColor: "#1F618D",
                        color: "#1F618D",
                      }}
                    >
                      Retención (.txt)
                      <input
                        hidden
                        accept=".txt"
                        type="file"
                        onChange={handleFileRetencion}
                      />
                    </Button>
                    {retencion && (
                      <TextField
                        disabled
                        value={retencion.name}
                        size="small"
                        sx={{ ml: 1.5, verticalAlign: "middle", width: 220 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachFileIcon
                                fontSize="small"
                                color="primary"
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Box
                sx={{
                  border: "2px dashed #90C4E4",
                  borderRadius: 2,
                  p: 2.5,
                  textAlign: "center",
                  backgroundColor: "#F4F8FB",
                  mb: 2,
                }}
              >
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<ContentPasteSearchIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    borderColor: "#1F618D",
                    color: "#1F618D",
                  }}
                >
                  Seleccionar archivo (.txt / .csv)
                  <input
                    hidden
                    type="file"
                    multiple
                    accept=".txt,.csv"
                    onChange={handleFileChange}
                  />
                </Button>
              </Box>
            )}

            {!esArbaOCdba && Array.from(fileList).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  mb={0.5}
                  display="block"
                >
                  ARCHIVOS A SUBIR
                </Typography>
                <Table size="small">
                  <TableBody>
                    {Array.from(fileList).map((file, i) => (
                      <TableRow
                        key={i}
                        sx={{ "&:last-child td": { border: 0 } }}
                      >
                        <TableCell padding="none" sx={{ width: 40 }}>
                          <IconButton
                            size="small"
                            onClick={() => removefiles(i)}
                          >
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <AttachFileIcon fontSize="small" color="action" />
                            <Typography variant="body2">{file.name}</Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ({(file.size / 1000).toFixed(2)} KB)
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {!puedeGuardar && (
              <Box sx={{ textAlign: "center", py: 2, color: "text.disabled" }}>
                <InboxIcon sx={{ fontSize: 32, mb: 0.5, opacity: 0.35 }} />
                <Typography variant="body2">
                  Seleccioná{" "}
                  {esArbaOCdba ? "los archivos requeridos" : "un archivo"} para
                  continuar
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #eee" }}>
        <Button
          onClick={props.onClose}
          variant="outlined"
          color="error"
          disabled={isSubmitted}
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          Volver
        </Button>
        {!isSubmitted && (
          <Button
            disabled={!puedeGuardar}
            onClick={handleSubmit}
            variant="contained"
            color="success"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              minWidth: "100px",
            }}
          >
            Guardar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default UploadPadron;
